import Annotation from '@zxy-cloud/react-image-annotation';
import React from 'react';
import { IWithReactRouterProps, withReactRouter } from '../../mixins/router/withReactRouter';

import { fetchGet } from '../../store/fetch';
import * as conditionHelper from '../../utils/conditionHelper';
import { craConditionToColorCode } from '../../utils/craConditionHelper';
import { AssetTypes as assetTypes } from '../../utils/enums/AssetTypes';
import { ramsWeightedPriorityToColorCode } from '../../utils/ramsWeightedPriorityToColorCode';
import { Component } from '../utils/Component';
import { showAssetInNewWindow } from '../utils/showAssetInNewWindow';

interface IAnnotationData {
    id: string;
    text: string;
    categoryId?: undefined | string;
    elementId: string;
    unitId: string;
    manifestationId: string;
    conditionAdvice: string;
    conditionScore: string;
    assetId: string;
    assetName: string;
    assetType: assetTypes;
    craConditionScore?: undefined | number;
    failureModeId?: undefined | string;
    failureMode?:
        | undefined
        | {
              id: string;
              ramsWeightedPriority: null | string;
          };
    targetSurveyId?: undefined | string;
}

interface IAnnotationGeometry {
    height: number;
    width: number;
    x: number;
    y: number;
    type: string;
}

interface IAnnotation {
    geometry?: undefined | IAnnotationGeometry;
    data?: undefined | IAnnotationData;
}

interface ISurveyImageAnnotationProps extends IWithReactRouterProps {
    surveyId?: string | undefined;
    objectId?: string | undefined;
    assetId: string;
    smallFont: boolean;
}

interface ISurveyImageAnnotationState {
    annotations?: IAnnotation[];
    annotation?: IAnnotation;
}

class SurveyImageAnnotationComponent extends Component<ISurveyImageAnnotationProps, ISurveyImageAnnotationState> {
    constructor(props: ISurveyImageAnnotationProps) {
        super(props);

        this.state = {
            annotation: {},
            annotations: [],
        };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override render() {
        return (
            <Annotation
                src={`/api/assets/${this.props.assetId}/download`}
                alt="Image with annotations"
                style={{ imageOrientation: 'from-image' }}
                annotations={this.state.annotations}
                disableAnnotation={true}
                value={this.state.annotation}
                renderContent={(props: any) => this.renderContent(props)}
                renderHighlight={(props: any) => this.renderHighlight(props)}
                renderOverlay={() => <div />}
                onChange={async (annotation: any) => this.onChange(annotation)}
            />
        );
    }

    private async onChange(annotation: any) {
        await this.setState({ annotation });
    }

    private renderContent({ annotation }: any) {
        const { geometry } = annotation as { geometry: IAnnotationGeometry };
        if (annotation.data && (annotation.data.conditionAdvice || annotation.data.assetId || annotation.data.targetSurveyId)) {
            return (
                <div
                    key={annotation.data.id}
                    style={{
                        background: annotation.data.targetSurveyId ? '#F500E4' : 'black',
                        color: 'white',
                        padding: 10,
                        position: 'absolute',
                        fontSize: 12,
                        zIndex: 100,
                        left: `${geometry.x}%`,
                        top: `${geometry.y + geometry.height}%`,
                    }}
                >
                    {annotation.data.assetId && (
                        <div>
                            <a href="#" onClick={(e) => this.onShowAsset(e, annotation.data as IAnnotationData)}>
                                <img
                                    src={this.getImageUrl(annotation.data as IAnnotationData)}
                                    className={'img-responsive'}
                                    title={annotation.data.name}
                                    width="240px"
                                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                                />
                            </a>
                        </div>
                    )}
                    {(annotation.data.targetSurveyId && (
                        <div>
                            <a
                                href="#"
                                style={{ color: '#FFFFFF' }}
                                className="fw600"
                                onClick={() => this.props.navigate(`/surveys/${annotation.data.targetSurveyId}`)}
                            >
                                Open
                            </a>
                        </div>
                    )) || <div style={{ width: '260px' }}>{annotation.data && annotation.data.conditionAdvice}</div>}
                </div>
            );
        }
        return null;
    }

    private renderHighlight({ key, annotation, active }: any) {
        const { geometry, data } = annotation;

        if (!geometry) {
            return null;
        }

        let fontSize = '13px';
        if (this.props.smallFont) {
            fontSize = '10px';
        }

        let borderColor = '#f500e4';
        if (annotation.data.failureMode) {
            borderColor = ramsWeightedPriorityToColorCode(annotation.data.failureMode.ramsWeightedPriority);
        } else if (annotation.data.conditionScore) {
            borderColor = conditionHelper.codeToColorCode(annotation.data.conditionScore, undefined);
        } else if (annotation.data.craConditionScore) {
            borderColor = craConditionToColorCode(annotation.data.craConditionScore);
        }

        return (
            <div
                key={key}
                style={{
                    fontSize,
                    border: `dashed 3px ${borderColor}`,
                    padding: '3px',
                    background: !active ? 'rgba(200, 200, 200, 0.5)' : 'rgba(200, 200, 200, 0.0)',
                    position: 'absolute',
                    left: `${geometry.x}%`,
                    top: `${geometry.y}%`,
                    height: `${geometry.height}%`,
                    width: `${geometry.width}%`,
                }}
            >
                {data.text && data.text !== '' && (
                    <span
                        style={{
                            color: borderColor,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            padding: '2px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        }}
                    >
                        {data.text}
                    </span>
                )}
            </div>
        );
    }

    private onShowAsset(e: React.MouseEvent<any>, annotation: IAnnotationData) {
        e.preventDefault();

        if (this.props.surveyId && this.props.surveyId !== 'undefined') {
            showAssetInNewWindow({ id: annotation.assetId, type: annotation.assetType }, { surveyId: this.props.surveyId });
        } else {
            showAssetInNewWindow({ id: annotation.assetId, type: annotation.assetType });
        }
    }

    private getImageUrl(annotation: IAnnotationData): string {
        if (annotation.assetType === assetTypes.MeasurementXLS) {
            return '/assets/img/excel_thmb.png';
        }
        if (annotation.assetType === assetTypes.MeasurementPDF) {
            return '/assets/img/pdf_thmb.png';
        }
        return `/api/assets/${annotation.assetId}/download?thumb=true`;
    }

    private async load() {
        const annotations: IAnnotation[] = [];
        let url = '';
        if (this.props.surveyId && this.props.surveyId !== 'undefined') {
            url = `/api/surveys/${this.props.surveyId}/observationPoints?is2dsource=true&twodsourceassetid=${this.props.assetId}`; // annotation available in survey
        } else if (this.props.objectId && this.props.objectId !== 'undefined') {
            url = `/api/objects/${this.props.objectId}/observationPoints?is2dsource=true&twodsourceassetid=${this.props.assetId}`; // annotation available in object
        }
        if (url !== '') {
            const result = await fetchGet(url);

            result.json.map((item: any) => {
                const annotation: IAnnotation = {
                    geometry: item['geometry2d'],
                    data: {
                        id: item.id,
                        text: item.title,
                        elementId: item.elementId,
                        unitId: item.unitId,
                        manifestationId: item.manifestationId,
                        assetId: item.assetId,
                        targetSurveyId: item.targetSurveyId,
                        assetName: item.assetName,
                        assetType: item.assetType,
                        conditionAdvice:
                            item.description || item.elementConditionAdvice || item.unitConditionAdvice || item.manifestationConditionAdvice,
                        conditionScore: item.elementConditionScore || item.unitConditionScore || item.manifestationConditionScore,
                        craConditionScore: this.getCorrectCraCondition(item),
                        failureMode: item.failureModeId
                            ? {
                                  id: item.failureModeId,
                                  ramsWeightedPriority:
                                      item.failureModeVerificationRamsWeightedPriority || item.failureModeAnalysisRamsWeightedPriority,
                              }
                            : undefined,
                    },
                };
                annotations.push(annotation);
            });
        }

        await this.setState({
            annotation: {},
            annotations,
        });
    }

    private getCorrectCraCondition(observationPoint: {
        unitConditionCraInspectionScore?: number;
        unitConditionCraInitialScore?: number;
        unitConditionCraHistoryScore?: number;
    }) {
        if (observationPoint.unitConditionCraInspectionScore) {
            return observationPoint.unitConditionCraInspectionScore;
        } else if (observationPoint.unitConditionCraInitialScore || observationPoint.unitConditionCraHistoryScore) {
            return observationPoint.unitConditionCraInitialScore! > observationPoint.unitConditionCraHistoryScore!
                ? observationPoint.unitConditionCraInitialScore
                : observationPoint.unitConditionCraHistoryScore;
        }
        return undefined;
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyImageAnnotation = withReactRouter(SurveyImageAnnotationComponent);
